import { GroupCallBanner } from "@/components/call/GroupCall";
import { useCallback, useEffect, useState } from "react";
import { PhotoViewer } from "@/components/PhotoViewer";
import { ReportButton } from "@/components/ReportButton";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Star, Waves, UtensilsCrossed, Compass } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, Crumbs, ErrorState, IllustrativeTag, Spinner, StatusBadge } from "@/components/common";
import { DetailNumbers } from "@/components/experience/Numbers";
import { StaysList } from "@/components/experience/StaysList";
import { useAuth } from "@/context/AuthContext";
import { ActionPanel } from "@/components/plan/ActionPanel";
import { ContributionTable } from "@/components/plan/ContributionTable";
import { GuestTermsList } from "@/components/plan/RequestDialog";
import { api, apiError, coverOf, formatDates, imageUrl, TYPE_SINGULAR } from "@/lib/api";

const ICONS = [Waves, UtensilsCrossed, Compass];

const Gallery = ({ exp, onChange }) => {
  const { user } = useAuth();
  const paths = exp.gallery?.length ? exp.gallery : exp.images || [];
  const imgs = paths.length ? paths.map(imageUrl) : [coverOf(exp)];
  const [open, setOpen] = useState(null);
  const real = paths.length > 0;
  const isHost = real && !exp.is_sample && (exp.my_role === "lead" || user?.role === "admin") && !["closed", "cancelled"].includes(exp.status);
  const canSetCover = isHost && paths.length > 1;
  const setCaption = async (path, caption) => {
    try { const { data } = await api.put(`/experiences/${exp.id}/captions`, { path, caption }); onChange(data); toast.success(caption ? "Caption saved" : "Caption removed"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setCover = async (path) => {
    try { const { data } = await api.post(`/experiences/${exp.id}/cover`, { path }); onChange(data); toast.success("Cover updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const Thumb = ({ i, className, children }) => (
    <div className={`group relative overflow-hidden rounded-xl ${className}`}>
      <button type="button" disabled={!real} onClick={() => setOpen(i)} aria-label={`Open photo ${i + 1} of ${imgs.length}`}
        className={`absolute inset-0 h-full w-full ${real ? "cursor-zoom-in" : "cursor-default"}`} data-testid={`gallery-photo-${i}`}>
        {children}
      </button>
      {canSetCover && i > 0 && (
        <button type="button" onClick={(e) => { e.stopPropagation(); setCover(paths[i]); }} aria-label="Make this the cover"
          className="absolute left-3 top-3 flex h-9 items-center gap-1.5 rounded-full bg-background/80 px-3 text-xs opacity-0 backdrop-blur transition-opacity focus:opacity-100 group-hover:opacity-100 sm:opacity-0 max-sm:opacity-100"
          data-testid={`gallery-set-cover-${i}`}><Star className="h-3.5 w-3.5" /> Make cover</button>
      )}
      {exp.captions?.[paths[i]] && <span className="pointer-events-none absolute bottom-3 left-3 max-w-[85%] truncate rounded-full bg-black/60 px-3 py-1 text-xs text-white backdrop-blur" data-testid={`gallery-caption-${i}`}>{exp.captions[paths[i]]}</span>}
      {canSetCover && i === 0 && <span className="absolute left-3 top-3 flex h-9 items-center gap-1.5 rounded-full bg-background/80 px-3 text-xs backdrop-blur" data-testid="gallery-cover-badge"><Star className="h-3.5 w-3.5 fill-current text-primary" /> Cover</span>}
    </div>
  );
  const img = (i, extra = "") => <img src={imgs[i]} alt={i === 0 ? exp.title : ""} loading={i ? "lazy" : undefined} className={`img-cover transition-transform duration-500 group-hover:scale-[1.03] ${extra}`} />;
  return (
    <>
      {imgs.length === 1 ? (
        <div data-testid="experience-gallery"><Thumb i={0} className="block aspect-[16/10] w-full sm:aspect-[21/10]">{img(0, "object-[center_60%]")}</Thumb></div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr]" data-testid="experience-gallery">
          <Thumb i={0} className="aspect-[16/10] sm:row-span-2 sm:aspect-auto sm:min-h-[420px]">{img(0)}
            <span className="absolute bottom-3 right-3 rounded-full bg-background/80 px-3 py-1 text-xs backdrop-blur sm:hidden">1 / {imgs.length}</span>
          </Thumb>
          {imgs.slice(1, 3).map((_, k) => (
            <Thumb key={k} i={k + 1} className="hidden aspect-[16/11] sm:block">{img(k + 1)}
              {k === 1 && imgs.length > 3 && <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-semibold" data-testid="gallery-more">+{imgs.length - 3} photos</span>}
            </Thumb>
          ))}
        </div>
      )}
      {open !== null && <PhotoViewer photos={imgs} paths={real && !exp.is_sample ? paths : undefined} index={open} onIndex={setOpen} onClose={() => setOpen(null)} alt={exp.title} onSetCover={canSetCover ? setCover : undefined} coverPath={paths[0]} captions={exp.captions} onCaption={isHost ? setCaption : undefined} />}
    </>
  );
};

const TabTrigger = ({ value, children }) => (
  <TabsTrigger value={value} data-testid={`tab-${value}`}
    className="relative h-12 rounded-none border-b-2 border-transparent bg-transparent px-0 text-[15px] tracking-wide text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none">
    {children}
  </TabsTrigger>
);

const PersonRow = ({ p, label }) => (
  <li className="flex items-center gap-4 py-3">
    <Avatar person={p} size={52} />
    <div className="min-w-0">
      {p.id ? <Link to={`/members/${p.id}`} className="font-display text-xl hover:text-primary">{p.name}</Link> : <p className="font-display text-xl">{p.name}</p>}
      <p className="text-sm text-muted-foreground">{label}{p.home_city ? ` · ${p.home_city}` : ""}</p>
    </div>
  </li>
);

const DetailTabs = ({ exp, onChange }) => (
  <Tabs defaultValue="idea" className="mt-8">
    <TabsList className="h-auto w-full justify-start gap-8 overflow-x-auto rounded-none border-b border-border bg-transparent p-0">
      <TabTrigger value="idea">The idea</TabTrigger>
      <TabTrigger value="cohosts">Co-hosts</TabTrigger>
      <TabTrigger value="contributions">Contributions</TabTrigger>
      <TabTrigger value="guests">Guests</TabTrigger>
    </TabsList>
    <TabsContent value="idea" className="pt-8" data-testid="tab-content-idea">
      <h2 className="font-display text-4xl leading-tight sm:text-5xl">{exp.summary}</h2>
      {exp.description && <p className="mt-5 max-w-2xl whitespace-pre-line text-foreground/85">{exp.description}</p>}
      {!exp.is_sample && <DetailNumbers exp={exp} />}
      {exp.highlights?.length > 0 && (
        <ul className="mt-8 grid gap-6 sm:grid-cols-3">
          {exp.highlights.map((h, i) => { const I = ICONS[i % 3]; return (
            <li key={h} className="flex items-center gap-4"><span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-foreground/40"><I className="h-6 w-6" strokeWidth={1.25} /></span><span className="font-display text-xl">{h}</span></li>
          ); })}
        </ul>
      )}
      <StaysList exp={exp} onChange={onChange} />
    </TabsContent>
    <TabsContent value="cohosts" className="pt-6" data-testid="tab-content-cohosts">
      <ul className="divide-y divide-border">{exp.circle.map((p, i) => <PersonRow key={p.id || i} p={p} label={p.role === "lead" ? "Lead host" : "Co-host"} />)}</ul>
      {exp.is_sample && <p className="mt-4 text-sm text-muted-foreground">The Genesis Angels are brand characters shown for illustration, not club members.</p>}
    </TabsContent>
    <TabsContent value="contributions" className="pt-6" data-testid="tab-content-contributions">
      {exp.plan ? <ContributionTable plan={exp.plan} sample={exp.is_sample} /> : (
        <p className="surface p-6 text-foreground/85">Contributions are agreed individually and are visible only to the co-host circle. Request to co-host to see and agree your share.</p>
      )}
    </TabsContent>
    <TabsContent value="guests" className="pt-6" data-testid="tab-content-guests">
      <p className="mb-6 text-foreground/85">{exp.guest_places ? `${exp.guest_count} of ${exp.guest_places} guest places filled.` : "No guest places are offered on this experience."}</p>
      {exp.guests?.length > 0 && <ul className="mb-6 divide-y divide-border">{exp.guests.map((g) => <PersonRow key={g.id} p={g} label="Guest" />)}</ul>}
      <div className="surface p-6"><GuestTermsList terms={exp.guest_terms} /></div>
    </TabsContent>
  </Tabs>
);

export default function ExperienceDetail() {
  const { id } = useParams();
  const [exp, setExp] = useState(null);
  const [error, setError] = useState("");
  const { user } = useAuth();

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/experiences/${id}`); setExp(data); setError(""); } catch (e) { setError(apiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="shell py-16"><ErrorState message={error} onRetry={load} /></div>;
  if (!exp) return <Spinner label="Opening experience" />;

  return (
    <div className="shell pb-20 pt-6 sm:pt-8" data-testid="experience-detail-page">
      <Link to="/discover" className="mb-4 inline-flex min-h-[44px] items-center gap-2 text-sm sm:hidden" data-testid="detail-back-link"><ArrowLeft className="h-4 w-4" /> Back</Link>
      <div className="hidden sm:block"><Crumbs items={[{ label: "Discover", to: "/discover" }, { label: exp.title }]} /></div>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-5xl leading-[0.95] lg:text-7xl" data-testid="experience-title">{exp.title}</h1>
          <p className="mt-3 text-lg text-foreground/85">{TYPE_SINGULAR[exp.type]} · {exp.destination} · {formatDates(exp)}</p>
        </div>
        <div className="flex items-center gap-3">{exp.is_sample && <IllustrativeTag />}
          {user?.role === "admin" && exp.my_role !== "lead" && !exp.is_sample && !["closed", "cancelled"].includes(exp.status) && <Link to={`/experiences/${exp.id}/edit`} className="btn-outline min-h-[40px] px-4 text-sm" data-testid="admin-edit-experience-link"><Pencil className="h-4 w-4" /> Edit (admin)</Link>}<StatusBadge status={exp.status} /></div>
      </div>
      <div className="mt-6 grid gap-x-8 gap-y-6 lg:grid-cols-[1fr_380px]">
        <div className="min-w-0 space-y-3 lg:col-start-1"><Gallery exp={exp} onChange={setExp} />
          {exp.video && <video src={imageUrl(exp.video)} controls preload="metadata" playsInline className="aspect-video w-full rounded-xl border border-border bg-black" data-testid="experience-video" />}
          {exp.video && exp.my_role !== "lead" && !exp.is_sample && <ReportButton kind="video" target={exp.video} testId="report-experience-video" />}</div>
        <div className="lg:col-start-2 lg:row-span-2 lg:row-start-1"><ActionPanel exp={exp} onChange={(d) => (d?.id ? setExp(d) : load())} /></div>
        <div className="min-w-0 lg:col-start-1">{exp.my_role && !exp.is_sample && <GroupCallBanner expId={exp.id} />}<DetailTabs exp={exp} onChange={setExp} /></div>
      </div>
    </div>
  );
}
